// WHAT THE TENANT'S SUSPENSION LETS THIS BROWSER DO (spec 15 §3.2, §4 S20;
// D-495, D-627).
//
// The web learns a suspension in ONE place: a middleware on the single API
// client (lib/api.ts) reads every 403 and 410 the tenant host answers and
// records what it says. Areas never inspect suspension codes themselves, and
// no area hook was edited for this (D-495 ruling 2).
//
//   * `403 TENANT_SUSPENDED` (an immediate suspension, or a grace window that
//     has run out) records `locked`: the office and the floor render S20, the
//     lock screen, instead of the screen asked for.
//   * `403 TENANT_SUSPENDED_GRACE` records `grace`: the shells show the red
//     read-only banner, and the shared submit button refuses to submit.
//   * `410 TENANT_PURGED` records `purged`: the static "workspace closed" page.
//
// The signed-in profile (`GET /v1/auth/profile`, read once per sign-in) also
// names an open grace window, so the banner is up before the first refused
// write rather than after it (D-627 ruling 3).
//
// The record is cleared by evidence, never by a timer: any successful answer
// ends `locked` and `purged` (the host serves again), and a successful write
// that is not session plumbing ends `grace` (grace lets `/v1/auth/*` through,
// so those prove nothing). While in grace or locked the profile is read again
// every two minutes, so a restore reaches a browser that only reads, and a
// locked one, which renders S20 in place of every screen and so makes no call
// of its own (D-629 ruling 5). The profile read is refused until the restore
// and answers after it, and that answer clears the record.

import { useSyncExternalStore } from "react";

export type SuspensionCategory = "billing" | "other";

export type TenantSuspension =
  | { readonly kind: "none" }
  | {
      readonly kind: "grace";
      readonly graceEndsAt: string;
      readonly category: SuspensionCategory;
      /** The tenant's own zone, when the profile named it. */
      readonly timezone?: string | undefined;
    }
  | { readonly kind: "locked"; readonly category: SuspensionCategory }
  | { readonly kind: "purged" };

const NONE: TenantSuspension = { kind: "none" };

let current: TenantSuspension = NONE;
const listeners = new Set<() => void>();

function emit(): void {
  for (const listener of listeners) listener();
}

export function tenantSuspension(): TenantSuspension {
  return current;
}

/** Record what the server just said. A grace record keeps a zone it already
 * knew when the new answer (a 403 body) carries none. */
export function noteSuspension(next: TenantSuspension): void {
  if (
    next.kind === "grace" &&
    current.kind === "grace" &&
    next.timezone === undefined &&
    current.timezone !== undefined
  ) {
    next = { ...next, timezone: current.timezone };
  }
  if (JSON.stringify(next) === JSON.stringify(current)) return;
  current = next;
  emit();
  scheduleGraceRecheck();
}

export function clearSuspension(): void {
  if (current.kind === "none") return;
  current = NONE;
  emit();
  scheduleGraceRecheck();
}

export function subscribeSuspension(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function useTenantSuspension(): TenantSuspension {
  return useSyncExternalStore(subscribeSuspension, tenantSuspension, tenantSuspension);
}

/** True while the tenant may be read but not written. The floor queue holds
 * its work and the office's submit button refuses. A locked or closed tenant
 * renders no screen to submit from at all. */
export function tenantWritesPaused(): boolean {
  return current.kind !== "none";
}

function category(value: unknown): SuspensionCategory {
  return value === "billing" ? "billing" : "other";
}

/** What a problem body says about the suspension, or null when it says
 * nothing (every other 403 and 410). */
export function suspensionFromProblem(status: number, body: unknown): TenantSuspension | null {
  if (typeof body !== "object" || body === null) return null;
  const problem = body as Record<string, unknown>;
  const code = problem["code"];
  if (status === 410 && code === "TENANT_PURGED") return { kind: "purged" };
  if (status !== 403) return null;
  if (code === "TENANT_SUSPENDED") {
    return { kind: "locked", category: category(problem["reason_category"]) };
  }
  if (code === "TENANT_SUSPENDED_GRACE" && typeof problem["grace_ends_at"] === "string") {
    return {
      kind: "grace",
      graceEndsAt: problem["grace_ends_at"],
      category: category(problem["reason_category"]),
    };
  }
  return null;
}

/** The profile's `tenant_suspension` (D-627 ruling 3), as a record. */
export function suspensionFromProfile(profile: unknown): TenantSuspension {
  if (typeof profile !== "object" || profile === null) return NONE;
  const raw = (profile as Record<string, unknown>)["tenant_suspension"];
  if (typeof raw !== "object" || raw === null) return NONE;
  const s = raw as Record<string, unknown>;
  if (s["mode"] !== "grace" || typeof s["grace_ends_at"] !== "string") return NONE;
  return {
    kind: "grace",
    graceEndsAt: s["grace_ends_at"],
    category: category(s["reason_category"]),
    timezone: typeof s["timezone"] === "string" ? s["timezone"] : undefined,
  };
}

const WRITE_METHODS = new Set(["POST", "PUT", "PATCH", "DELETE"]);

/** Does a successful answer to this request prove the suspension is over? */
export function successEndsSuspension(
  state: TenantSuspension,
  method: string,
  pathname: string,
): boolean {
  if (state.kind === "locked" || state.kind === "purged") return true;
  if (state.kind !== "grace") return false;
  return WRITE_METHODS.has(method.toUpperCase()) && !pathname.startsWith("/v1/auth/");
}

/** What the client middleware in lib/api.ts runs on every answer (D-495
 * ruling 2). It reads a clone, so the caller's own read of the body is
 * untouched. */
export async function observeTenantResponse(request: Request, response: Response): Promise<void> {
  if (response.ok) {
    const path = new URL(request.url, "http://local.invalid").pathname;
    if (successEndsSuspension(current, request.method, path)) clearSuspension();
    return;
  }
  if (response.status !== 403 && response.status !== 410) return;
  try {
    const body: unknown = await response.clone().json();
    const next = suspensionFromProblem(response.status, body);
    if (next !== null) noteSuspension(next);
  } catch {
    // Not JSON: not a problem document, so not about suspension.
  }
}

// ---- the grace re-check ----------------------------------------------------

/** How often a browser in grace or locked asks whether the account was
 * restored. */
export const GRACE_RECHECK_MS = 120_000;
let recheck: ReturnType<typeof setInterval> | undefined;
let readProfile: (() => Promise<unknown>) | undefined;

/** What the profile reader answers when the host served the read and the
 * session is gone (a 401). A locked host refuses with a 403 before it reads
 * any session, so a 401 proves the host serves again: an immediate suspension
 * revoked the session, and the restore that followed brought the host back
 * with nobody signed in (D-629 ruling 5). The lock ends, and the screens'
 * own reads then take the member to sign in. Grace answers the profile with
 * no session as a 401 too while still read-only, so a 401 ends only a lock. */
export const PROFILE_SIGNED_OUT = "signed_out" as const;

/** lib/api.ts hands the store its profile read, so this module imports no
 * client and the client imports this one without a cycle. */
export function setSuspensionProfileReader(reader: () => Promise<unknown>): void {
  readProfile = reader;
}

function scheduleGraceRecheck(): void {
  // Purged is final: nothing to re-check. Grace and locked both end on a
  // restore no screen of theirs would otherwise ask about.
  const wanted =
    (current.kind === "grace" || current.kind === "locked") && readProfile !== undefined;
  if (wanted && recheck === undefined) {
    recheck = setInterval(() => {
      const reader = readProfile;
      if (reader === undefined) return;
      void reader()
        .then((profile) => {
          if (profile === undefined) return;
          if (profile === PROFILE_SIGNED_OUT) {
            if (current.kind === "locked") clearSuspension();
            return;
          }
          const next = suspensionFromProfile(profile);
          if (next.kind === "none") clearSuspension();
          else noteSuspension(next);
        })
        .catch(() => undefined);
    }, GRACE_RECHECK_MS);
  } else if (!wanted && recheck !== undefined) {
    clearInterval(recheck);
    recheck = undefined;
  }
}

/** Tests only: forget everything this module holds. */
export function resetSuspensionForTests(): void {
  current = NONE;
  if (recheck !== undefined) clearInterval(recheck);
  recheck = undefined;
  emit();
}
